import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { getSaasParams } from '@/lib/api/saasApi';
import { loginApi, registerApi } from '@/lib/auth/authApi';
import { passwordRequirements, strengthColor } from '@/lib/form/passwordStrenght.ts';
import { queryKeys } from '@/lib/query/queryKeys';
import { safeInternalPath } from '@/lib/url-params';
import { registerSchema } from '@/lib/validation/registerSchema';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store.ts';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  CircleDot,
  Eye,
  EyeOff,
  Gift,
  Lock,
  Mail,
  Trophy,
  User,
  X,
} from 'lucide-react';

type ProfileKey = 'joueur' | 'club' | 'organisateur';

export default function Register() {
  const { data: saasParams, isLoading: saasLoading } = useQuery({
    queryKey: queryKeys.saasParams(),
    queryFn: getSaasParams,
    staleTime: 60_000,
  });

  const [searchParams] = useSearchParams();
  const referralEnabled = useSaasStore(s => s.referralEnabled);
  const fromSource = searchParams.get('fromSource');
  const redirectTo = safeInternalPath(searchParams.get('redirectTo'));
  const refCode = searchParams.get('ref') || '';
  const claimClubId = searchParams.get('claimClubId') || '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<ProfileKey | null>(claimClubId ? 'club' : null);
  const [isAutoLoggedIn, setIsAutoLoggedIn] = useState(false);
  const [referralCode, setReferralCode] = useState(refCode);

  const { login } = useAuthStore();
  const navigate = useNavigate();

  const strength = useMemo(() => passwordRequirements.filter(req => req.test(password)).length, [password]);

  const handleFirstNameBlur = () => {
    if (!username && firstName) {
      const suggested = `${firstName.toLowerCase()}.${lastName.toLowerCase()}`.replace(/[^a-z0-9.]/g, '');
      if (suggested) setUsername(suggested);
    }
  };

  // dest : page vers laquelle l'utilisateur doit atterrir après le login.
  // Si non précisé, on utilise le redirectTo d'origine (comportement par défaut).
  const buildLoginUrl = (dest?: string) => {
    const params = new URLSearchParams();
    if (fromSource) params.set('fromSource', fromSource);
    params.set('redirectTo', dest ?? redirectTo);
    const qs = params.toString();
    return `${ROUTES.LOGIN}${qs ? `?${qs}` : ''}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const result = registerSchema.safeParse({
      firstName,
      lastName,
      username,
      email,
      password,
      confirmPassword,
      referralCode: referralCode || undefined,
      acceptCgu,
    });

    if (!result.success) {
      const errs: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0]?.toString();
        if (key && !errs[key]) errs[key] = issue.message;
      }
      setFieldErrors(errs);
      setError(Object.values(errs)[0] ?? 'Veuillez corriger les erreurs');
      return;
    }

    setIsSubmitting(true);
    try {
      const d = result.data;
      await registerApi({
        email: d.email,
        username: d.username,
        password: d.password,
        firstName: d.firstName,
        lastName: d.lastName,
        referralCode: d.referralCode || undefined,
      });
      // Auto-login : évite de renvoyer l'utilisateur sur la page de connexion
      try {
        const data = await loginApi({ email: d.email, password: d.password });
        if (!data.requiresMfa && data.user && data.accessToken) {
          login(data.user, data.accessToken);
        }
        setIsAutoLoggedIn(true);
      } catch {
        // En cas d'échec, l'utilisateur devra se connecter manuellement (fallback)
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'inscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const profiles: Record<
    ProfileKey,
    {
      icon: React.ElementType;
      title: string;
      desc: string;
      to: string;
      directRoute: string;
      color: string;
      iconBg: string;
      cardBorder: string;
      cardHover: string;
      confirmBtn: string;
      canDo: string[];
      cannotDo: string[];
    }
  > = {
    joueur: {
      icon: User,
      title: 'Je suis joueur',
      desc: "Rechercher des concours, m'inscrire en équipe",
      to: buildLoginUrl('/'),
      directRoute: redirectTo,
      color: 'text-emerald-600',
      iconBg: 'bg-emerald-100',
      cardBorder: 'border-emerald-100 hover:border-emerald-300',
      cardHover: 'hover:bg-emerald-50',
      confirmBtn: 'bg-emerald-600 hover:bg-emerald-700',
      canDo: [
        "Rechercher et s'inscrire à des concours",
        'Rejoindre un club comme membre',
        'Consulter votre profil joueur et vos résultats',
        'Suivre votre palmarès et vos statistiques',
      ],
      cannotDo: [
        'Créer ou administrer un club (rôle Gestionnaire requis)',
        'Organiser des concours (rôle Organisateur requis)',
        "Gérer des inscriptions d'équipes en tant qu'administrateur",
      ],
    },
    club: {
      icon: Building2,
      title: 'Je gère un club',
      desc: 'Créer mon club, gérer mes membres et équipes',
      to: buildLoginUrl(
        claimClubId ? `/creer-mon-club?claimClubId=${encodeURIComponent(claimClubId)}` : '/creer-mon-club',
      ),
      directRoute: claimClubId
        ? `${ROUTES.CREATE_CLUB_ONBOARDING}?claimClubId=${encodeURIComponent(claimClubId)}`
        : ROUTES.CREATE_CLUB_ONBOARDING,
      color: 'text-blue-600',
      iconBg: 'bg-blue-100',
      cardBorder: 'border-blue-100 hover:border-blue-300',
      cardHover: 'hover:bg-blue-50',
      confirmBtn: 'bg-blue-600 hover:bg-blue-700',
      canDo: [
        'Créer et administrer votre club',
        'Gérer les membres et les équipes du club',
        'Inscrire vos équipes à des concours',
        'Accéder au tableau de bord de gestion du club',
      ],
      cannotDo: [
        'Organiser vos propres concours (rôle Organisateur requis)',
        "Participer comme simple joueur dans d'autres clubs",
        'Gérer plusieurs clubs simultanément avec ce compte',
      ],
    },
    organisateur: {
      icon: Trophy,
      title: "J'organise des concours",
      desc: 'Créer et gérer des compétitions de pétanque',
      to: buildLoginUrl('/creer-mon-organisation'),
      directRoute: ROUTES.CREATE_ORG_ONBOARDING,
      color: 'text-orange-600',
      iconBg: 'bg-orange-100',
      cardBorder: 'border-orange-100 hover:border-orange-300',
      cardHover: 'hover:bg-orange-50',
      confirmBtn: 'bg-orange-600 hover:bg-orange-700',
      canDo: [
        'Créer et gérer une organisation sportive',
        'Organiser des concours et compétitions',
        'Affilier des clubs à votre organisation',
        'Accéder aux outils de gestion avancés et rapports',
      ],
      cannotDo: [
        'Administrer directement un club affilié (rôle distinct)',
        'Participer comme joueur individuel à des concours',
        'Modifier les effectifs des clubs de votre organisation',
      ],
    },
  };

  if (saasLoading) {
    return (
      <section className='min-h-screen bg-gradient-to-br from-emerald-600 to-emerald-400 flex items-center justify-center'>
        <div className='w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin' />
      </section>
    );
  }

  if (saasParams && !saasParams.openRegister) {
    return (
      <section className='min-h-screen bg-gradient-to-br from-emerald-600 to-emerald-400 flex flex-col items-center justify-center px-6'>
        <div className='flex items-center gap-3 mb-8'>
          <CircleDot className='w-10 h-10 text-white' />
          <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
        </div>
        <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl text-center'>
          <div className='w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4'>
            <Lock className='w-8 h-8 text-amber-600' />
          </div>
          <h2 className='text-2xl font-extrabold text-slate-900 mb-2'>Inscriptions fermées</h2>
          <p className='text-slate-500 text-sm mb-6'>
            Les inscriptions sont temporairement fermées. Veuillez réessayer ultérieurement.
          </p>
          <Link
            to={ROUTES.LOGIN}
            className='inline-block bg-emerald-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-emerald-700 transition-colors'
          >
            Se connecter
          </Link>
        </div>
      </section>
    );
  }

  if (success) {
    const profile = selectedProfile ? profiles[selectedProfile] : null;

    return (
      <section className='min-h-screen bg-gradient-to-br from-emerald-600 to-emerald-400 flex flex-col items-center justify-center px-6 pt-20 pb-10'>
        <div className='flex items-center gap-3 mb-8'>
          <CircleDot className='w-10 h-10 text-white' />
          <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
        </div>

        <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl'>
          {/* Header succès */}
          <div className='flex flex-col items-center text-center mb-6'>
            <div className='w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4'>
              <Check className='w-8 h-8 text-emerald-600' />
            </div>
            <h2 className='text-2xl font-extrabold text-slate-900 mb-1'>Compte créé !</h2>
            <p className='text-slate-500 text-sm'>
              {selectedProfile ? 'Confirmez votre choix de profil' : 'Quel est votre profil sur BoulesConnect ?'}
            </p>
          </div>

          {/* Étape 1 — choix du profil */}
          {!selectedProfile && (
            <div className='space-y-3'>
              {(Object.entries(profiles) as [ProfileKey, (typeof profiles)[ProfileKey]][]).map(([key, p]) => (
                <button
                  key={key}
                  type='button'
                  onClick={() => setSelectedProfile(key)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all group text-left ${p.cardBorder} ${p.cardHover} cursor-pointer`}
                >
                  <div className={`w-10 h-10 ${p.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                    <p.icon className={`w-5 h-5 ${p.color}`} />
                  </div>
                  <div className='flex-1'>
                    <span className='font-bold text-slate-900 text-sm block'>{p.title}</span>
                    <span className='text-xs text-slate-500'>{p.desc}</span>
                  </div>
                  <ChevronRight className='w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors' />
                </button>
              ))}
            </div>
          )}

          {/* Étape 2 — confirmation */}
          {selectedProfile && profile && (
            <div>
              {/* Badge profil sélectionné */}
              <div className={`flex items-center gap-3 p-3 rounded-xl ${profile.iconBg} mb-5`}>
                <div className='w-9 h-9 bg-white/60 rounded-lg flex items-center justify-center shrink-0'>
                  <profile.icon className={`w-5 h-5 ${profile.color}`} />
                </div>
                <span className={`font-bold text-sm ${profile.color}`}>{profile.title}</span>
              </div>

              {/* Ce que vous pouvez faire */}
              <div className='mb-4'>
                <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>
                  Avec ce profil, vous pourrez
                </p>
                <ul className='space-y-1.5'>
                  {profile.canDo.map((item, i) => (
                    <li key={i} className='flex items-start gap-2 text-sm text-slate-700'>
                      <Check className='w-4 h-4 text-emerald-500 shrink-0 mt-0.5' />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Ce qui ne sera pas possible */}
              <div className='mb-6 p-3 bg-slate-50 rounded-xl border border-slate-100'>
                <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>
                  Ce qui ne sera pas possible
                </p>
                <ul className='space-y-1.5'>
                  {profile.cannotDo.map((item, i) => (
                    <li key={i} className='flex items-start gap-2 text-sm text-slate-500'>
                      <X className='w-4 h-4 text-red-400 shrink-0 mt-0.5' />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div className='flex gap-3'>
                <button
                  type='button'
                  onClick={() => setSelectedProfile(null)}
                  className='flex items-center gap-1.5 px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer'
                >
                  <ArrowLeft className='w-4 h-4' />
                  Retour
                </button>
                <button
                  type='button'
                  onClick={() => navigate(isAutoLoggedIn ? profile.directRoute : profile.to)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-sm transition-colors ${profile.confirmBtn} cursor-pointer`}
                >
                  Confirmer et continuer
                  <ChevronRight className='w-4 h-4' />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className='min-h-screen bg-gradient-to-br from-emerald-600 to-emerald-400 flex flex-col items-center justify-center px-6 pt-20 pb-10'>
      {/* Brand */}
      <div className='flex items-center gap-3 mb-8'>
        <CircleDot className='w-10 h-10 text-white' />
        <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
      </div>

      {/* Card */}
      <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl'>
        <h2 className='text-2xl font-extrabold text-slate-900 text-center mb-8'>Créer un compte</h2>

        {error && (
          <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>{error}</div>
        )}

        <form onSubmit={handleSubmit} className='space-y-5'>
          {/* Prénom + Nom */}
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label htmlFor='firstName' className='block text-sm font-semibold text-slate-700 mb-2'>
                Prénom
              </label>
              <input
                id='firstName'
                type='text'
                required
                maxLength={50}
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                onBlur={handleFirstNameBlur}
                placeholder='Jean'
                className={`w-full px-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${fieldErrors.firstName ? 'border-red-300' : 'border-slate-200'}`}
              />
              {fieldErrors.firstName && <p className='text-xs text-red-500 mt-1'>{fieldErrors.firstName}</p>}
            </div>
            <div>
              <label htmlFor='lastName' className='block text-sm font-semibold text-slate-700 mb-2'>
                Nom
              </label>
              <input
                id='lastName'
                type='text'
                required
                maxLength={50}
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder='Dupont'
                className={`w-full px-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${fieldErrors.lastName ? 'border-red-300' : 'border-slate-200'}`}
              />
              {fieldErrors.lastName && <p className='text-xs text-red-500 mt-1'>{fieldErrors.lastName}</p>}
            </div>
          </div>

          {/* Username */}
          <div>
            <label htmlFor='username' className='block text-sm font-semibold text-slate-700 mb-2'>
              Nom d&apos;utilisateur
            </label>
            <div className='relative'>
              <User className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
              <input
                id='username'
                type='text'
                required
                minLength={3}
                maxLength={30}
                value={username}
                onChange={e => setUsername(e.target.value.toLowerCase())}
                placeholder='jean.dupont'
                className={`w-full pl-12 pr-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${fieldErrors.username ? 'border-red-300' : 'border-slate-200'}`}
              />
            </div>
            {fieldErrors.username && <p className='text-xs text-red-500 mt-1'>{fieldErrors.username}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor='email' className='block text-sm font-semibold text-slate-700 mb-2'>
              Adresse email
            </label>
            <div className='relative'>
              <Mail className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
              <input
                id='email'
                type='email'
                required
                maxLength={255}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='votre@email.com'
                className={`w-full pl-12 pr-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${fieldErrors.email ? 'border-red-300' : 'border-slate-200'}`}
              />
            </div>
            {fieldErrors.email && <p className='text-xs text-red-500 mt-1'>{fieldErrors.email}</p>}
          </div>

          {/* Mot de passe */}
          <div>
            <label htmlFor='password' className='block text-sm font-semibold text-slate-700 mb-2'>
              Mot de passe
            </label>
            <div className='relative'>
              <Lock className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                required
                minLength={8}
                maxLength={128}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Créez un mot de passe'
                className={`w-full pl-12 pr-12 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${fieldErrors.password ? 'border-red-300' : 'border-slate-200'}`}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
              >
                {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            </div>

            {password.length > 0 && (
              <div className='mt-2'>
                <div className='flex gap-1 mb-1.5'>
                  {[0, 1, 2].map(i => (
                    <div key={i} className={`flex-1 h-1 rounded-full ${strengthColor(strength, i + 1)}`} />
                  ))}
                </div>
                <ul className='space-y-0.5'>
                  {passwordRequirements.map((req, i) => (
                    <li
                      key={i}
                      className={`flex items-center gap-1.5 text-xs ${req.test(password) ? 'text-emerald-600' : 'text-slate-400'}`}
                    >
                      <Check className='w-3 h-3' />
                      {req.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Confirmation */}
          <div>
            <label htmlFor='confirmPassword' className='block text-sm font-semibold text-slate-700 mb-2'>
              Confirmer le mot de passe
            </label>
            <div className='relative'>
              <Lock className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
              <input
                id='confirmPassword'
                type={showConfirm ? 'text' : 'password'}
                required
                maxLength={128}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Répétez le mot de passe'
                className={`w-full pl-12 pr-12 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${fieldErrors.confirmPassword ? 'border-red-300' : 'border-slate-200'}`}
              />
              <button
                type='button'
                onClick={() => setShowConfirm(!showConfirm)}
                className='absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
              >
                {showConfirm ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            </div>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className='text-xs text-red-500 mt-1'>Les mots de passe ne correspondent pas</p>
            )}
          </div>

          {/* Code parrainage */}
          {referralEnabled && (
            <div>
              <label htmlFor='referralCode' className='block text-sm font-semibold text-slate-700 mb-2'>
                Code parrainage <span className='font-normal text-slate-400'>(optionnel)</span>
              </label>
              <div className='relative'>
                <Gift className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
                <input
                  id='referralCode'
                  type='text'
                  maxLength={20}
                  value={referralCode}
                  onChange={e => setReferralCode(e.target.value.toUpperCase())}
                  placeholder='Ex : ABC123'
                  className='w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
                />
              </div>
            </div>
          )}

          {/* CGU */}
          <label className='flex items-start gap-3 cursor-pointer'>
            <input
              type='checkbox'
              required
              checked={acceptCgu}
              onChange={e => setAcceptCgu(e.target.checked)}
              className='w-4 h-4 mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500'
            />
            <span className='text-sm text-slate-600'>
              J'accepte les{' '}
              <a
                href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/cgu-cgv`}
                target='_blank'
                rel='noreferrer'
                className='text-emerald-600 font-semibold hover:underline'
              >
                CGU
              </a>{' '}
              et la{' '}
              <a
                href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/politique-rgpd`}
                target='_blank'
                rel='noreferrer'
                className='text-emerald-600 font-semibold hover:underline'
              >
                Politique de confidentialité
              </a>
            </span>
          </label>

          {/* Submit */}
          <button
            type='submit'
            disabled={isSubmitting || !acceptCgu}
            className='cursor-pointer w-full bg-emerald-600 text-white font-bold py-3 rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
          >
            {isSubmitting ? 'Inscription…' : 'Créer mon compte'}
          </button>
        </form>

        {/* Lien login */}
        <p className='text-center text-sm text-slate-500 mt-6'>
          Déjà un compte ?{' '}
          <Link to={buildLoginUrl()} className='text-emerald-600 font-semibold hover:underline'>
            Se connecter
          </Link>
        </p>
      </div>

      {/* Footer */}
      <div className='flex items-center gap-4 mt-8 text-sm text-white/70'>
        <a
          href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/support`}
          className='hover:text-white transition-colors'
        >
          Aide
        </a>
        <span>&middot;</span>
        <a
          href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/politique-rgpd`}
          className='hover:text-white transition-colors'
        >
          Confidentialité
        </a>
        <span>&middot;</span>
        <a
          href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/cgu-cgv`}
          className='hover:text-white transition-colors'
        >
          Conditions
        </a>
      </div>
    </section>
  );
}
